import { useCallback, useMemo } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import { Box } from '@mui/material';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import WidgetContainer from './WidgetContainer';

/**
 * There is one stored layout and its positions are 12-column coordinates.
 * Declaring md/sm breakpoints let the grid recompact that layout for a narrower
 * container and report the result back through onLayoutChange — which was
 * persisted, so opening a workspace below 1200px permanently rewrote every
 * saved position into the narrower column count, and widening again did not
 * bring it back. A single breakpoint at width 0 keeps one coordinate space: a
 * narrow container gets narrower columns, never a different layout.
 */
const BREAKPOINTS = { lg: 0 };
const COLS = { lg: 12 };

export default function WorkspaceGrid({
  widgets, recordings, recordingMetadata, eventsMap,
  rules, activeRules,
  globalRules, globalActiveRuleIds, tabRules, tabActiveRuleIds,
  onToggleGlobalRule, onToggleTabRule, onCreateRule,
  workspaceNotes, workspaceMarkers, tabNotes, tabMarkers,
  onUpdateNotes, onUpdateMarkers, onUpdateTabNotes, onUpdateTabMarkers,
  onRemoveWidget, onUpdateWidget, onLayoutChange,
  otherTabsRules,
}) {
  const { containerRef, width } = useContainerWidth();

  const layout = widgets.map((w) => ({
    i: w.id,
    x: w.position?.x || 0,
    y: w.position?.y || 0,
    w: w.position?.w || 4,
    h: w.position?.h || 3,
    minW: 2,
    minH: 2,
  }));

  /**
   * Persist only what a gesture produced. The grid's own onLayoutChange also
   * fires on mount and whenever it re-measures, which is how its compaction
   * used to be written back over the user's saved positions.
   */
  const handleGestureEnd = useCallback((newLayout) => {
    onLayoutChange(widgets.map((w) => {
      const item = newLayout.find((l) => l.i === w.id);
      return item ? { ...w, position: { x: item.x, y: item.y, w: item.w, h: item.h } } : w;
    }));
  }, [widgets, onLayoutChange]);

  // Memoize widget elements so they don't re-create when WorkspaceGrid
  // re-renders from timeline ticks or other unrelated state changes.
  const widgetElements = useMemo(() =>
    widgets.map((widget) => (
      <div key={widget.id} className="drag-handle">
        <WidgetContainer
          widget={widget}
          recordings={recordings}
          recordingMetadata={recordingMetadata}
          eventsMap={eventsMap}
          rules={rules}
          activeRules={activeRules}
          globalRules={globalRules}
          globalActiveRuleIds={globalActiveRuleIds}
          tabRules={tabRules}
          tabActiveRuleIds={tabActiveRuleIds}
          onToggleGlobalRule={onToggleGlobalRule}
          onToggleTabRule={onToggleTabRule}
          onCreateRule={onCreateRule}
          workspaceNotes={workspaceNotes}
          workspaceMarkers={workspaceMarkers}
          tabNotes={tabNotes}
          tabMarkers={tabMarkers}
          onUpdateNotes={onUpdateNotes}
          onUpdateMarkers={onUpdateMarkers}
          onUpdateTabNotes={onUpdateTabNotes}
          onUpdateTabMarkers={onUpdateTabMarkers}
          onRemove={onRemoveWidget}
          onUpdate={onUpdateWidget}
          otherTabsRules={otherTabsRules}
        />
      </div>
    )),
    [widgets, recordings, recordingMetadata, eventsMap,
     rules, activeRules, globalRules, globalActiveRuleIds,
     tabRules, tabActiveRuleIds,
     onToggleGlobalRule, onToggleTabRule, onCreateRule,
     workspaceNotes, workspaceMarkers, tabNotes, tabMarkers,
     onUpdateNotes, onUpdateMarkers, onUpdateTabNotes, onUpdateTabMarkers,
     onRemoveWidget, onUpdateWidget, otherTabsRules]
  );

  return (
    <Box sx={{ position: 'relative' }} ref={containerRef}>
      {width > 0 && (
        <ResponsiveGridLayout
          width={width}
          layouts={{ lg: layout }}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={80}
          onDragStop={handleGestureEnd}
          onResizeStop={handleGestureEnd}
          draggableHandle=".drag-handle"
          isResizable
          isDraggable
        >
          {widgetElements}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}
